import { Props as BaseProps, BaseCampaign } from './CampaignsTable';
import { DragAndDropTable, DragTableProps, getApplicationStyleVariables } from '@round/whitelabel-ui';
import styles from './CampaignsTable.module.css';
import cn from 'classnames';

type Props<TRow extends BaseCampaign> = Omit<BaseProps<TRow>, 'onDragEnd'> & DragTableProps;

const DragAndDropCampaignsTable = <TRow extends BaseCampaign>({
    data,
    isLoading,
    hasError,
    className,
    onDragEnd,
    ...props
}: Props<TRow>) => {
    const emptyRows = new Array(10).fill({}) as TRow[];
    const rows = isLoading ? data.concat(emptyRows) : data;

    return (
        <DragAndDropTable
            data={rows}
            enableExpanding
            manualExpanding
            className={cn(styles.table, className, { [styles.empty]: !data.length && !isLoading })}
            getRowClassName={(row) =>
                cn(getApplicationStyleVariables(row.original.managed_by), {
                    [styles.expandedRow]: row.getIsExpanded(),
                    [styles.managedByRoundExpandedRow]: row.getIsExpanded() && row.original.managed_by === 'round',
                    [styles.managedByMicrowaveExpandedRow]:
                        row.getIsExpanded() && row.original.managed_by === 'microwave',
                })
            }
            getRowId={(original, index) => original.id?.toString() ?? `skeleton-${index}`}
            noDataLabel={
                isLoading ? undefined : (
                    <div className={styles.noDataContainer} aria-label="no campaigns">
                        {hasError ? 'Could not load campaigns' : 'No campaigns yet'}
                    </div>
                )
            }
            onDragEnd={(...props) => {
                onDragEnd(...props);
                // Undo the height freezing done in the DragHandle onMouseDown event
                const container = document.querySelector('[data-draggable-container="true"]');
                if (!container || !(container instanceof HTMLElement)) {
                    return;
                }

                container.style.minHeight = '';
            }}
            onDragUpdate={(update) => {
                if (update.destination?.index === undefined) {
                    return;
                }

                // Collapse the row that was dragged over. This prevents visual bugs with a static expanded table.
                // Only changed when a row actually collapses, since every change re-renders the whole table.
                const { expandedState, onExpandedChange } = props;
                if (expandedState === true) {
                    onExpandedChange?.({});
                    return;
                }

                const rowId = data[update.destination.index]?.id;
                if (rowId !== undefined && expandedState?.[rowId]) {
                    onExpandedChange?.({ ...expandedState, [rowId]: false });
                }
            }}
            {...props}
        />
    );
};

export default DragAndDropCampaignsTable;
